import { useState, type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import { useColorScheme } from '@mui/material/styles'
import { displayName, initials } from '@/lib/format'
import { useAuth } from '@/auth/useAuth'
import { useSignOutConfirmation } from '@/auth/useSignOutConfirmation'
import WorkIcon from '@mui/icons-material/Work';

/** Modes understood by MUI's `useColorScheme`; `system` follows the OS setting. */
type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeOption {
  mode: ThemeMode
  label: string
  icon: ReactNode
}

const THEME_OPTIONS: readonly ThemeOption[] = [
  { mode: 'system', label: 'System', icon: <SettingsBrightnessOutlinedIcon fontSize="small" /> },
  { mode: 'light', label: 'Light', icon: <LightModeOutlinedIcon fontSize="small" /> },
  { mode: 'dark', label: 'Dark', icon: <DarkModeOutlinedIcon fontSize="small" /> },
]

/**
 * Account menu in the app bar.
 *
 * Follows Material UI's "account menu" pattern: the dropdown right-aligns to
 * the avatar with a pointer arrow, and closes on any item click. The Theme
 * item opens a nested menu that switches between the light, dark and system
 * colour schemes via `useColorScheme()`.
 */
export function UserMenu() {
  const { user } = useAuth()
  const { requestSignOut, dialog } = useSignOutConfirmation()
  const { mode, setMode } = useColorScheme()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [themeAnchorEl, setThemeAnchorEl] = useState<HTMLElement | null>(null)

  if (!user) return null
  const open = Boolean(anchorEl)
  const themeOpen = Boolean(themeAnchorEl)
  const activeMode: ThemeMode = mode ?? 'system'

  const handleClose = () => {
    setAnchorEl(null)
    setThemeAnchorEl(null)
  }

  const handleOpenTheme = (event: MouseEvent<HTMLElement>) => {
    // Keep the account menu open while the nested menu is on screen.
    event.stopPropagation()
    setThemeAnchorEl(event.currentTarget)
  }

  const handleSelectTheme = (next: ThemeMode) => {
    setMode(next)
    handleClose()
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title="Account settings">
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open}
            aria-label="Open account menu"
          >
            <Avatar
              src={user.avatarUrl ?? undefined}
              alt={displayName(user)}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}
            >
              {initials(user)}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              minWidth: 240,
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => navigate('/profile')}>
          <Avatar
            src={user.avatarUrl ?? undefined}
            alt={displayName(user)}
            sx={{ bgcolor: 'primary.main', fontSize: 14 }}
          >
            {initials(user)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {displayName(user)}
            </Typography>
            {user.role ? (
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" variant="outlined" icon={<WorkIcon />} label={user.role.name} />
              </Box>
            ) : null}
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem
          onClick={handleOpenTheme}
          aria-haspopup="true"
          aria-expanded={themeOpen}
          aria-controls={themeOpen ? 'theme-menu' : undefined}
        >
          <ListItemIcon>
            <PaletteOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Theme" />
          <KeyboardArrowRightIcon fontSize="small" sx={{ ml: 2, color: 'text.secondary' }} />
        </MenuItem>
        <MenuItem onClick={requestSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <Menu
        id="theme-menu"
        anchorEl={themeAnchorEl}
        open={themeOpen}
        onClose={() => setThemeAnchorEl(null)}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: { minWidth: 180, filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))' },
          },
        }}
      >
        {THEME_OPTIONS.map((option) => (
          <MenuItem
            key={option.mode}
            selected={activeMode === option.mode}
            onClick={() => handleSelectTheme(option.mode)}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {dialog}
    </>
  )
}
